import { start } from "repl";
import { ActionType, ApprovalPayload, BulkTaskUpdateModel } from "./../domain/task.model";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApplicationResponse } from "@src/common/domain/common.model";
import api from "@src/common/services/api.service";
import AppConstants from "@src/utils/app.constants";
import { ProjectType } from "@src/utils/data-global";
import { updateDependency } from "@src/views/create-task/slices/task-service";

import {
  DependencyModelRef,
  TaskModelRef,
  TaskRevisionModelRef,
  TaskStore,
} from "@src/views/project-tasks/domain/task.model";
import { coolGray } from "tailwindcss/colors";



const addDependencyName = (data: TaskModelRef | undefined) => {
  const dependencyData = data?.dependencies || [];
  const newDependencyData = dependencyData.map((d) => {
    return {
      ...d,
      dependencyName:
        d.dependencyType === "SS"
          ? "Start to Start"
          : d.dependencyType === "SF"
          ? "Start to Finish"
          : d.dependencyType === "FS"
          ? "Finish to Start"
          : "Finish to Finish",
    };
  });
  return { ...data, dependencies: newDependencyData };
};

export const deleteProject = createAsyncThunk(
  "project/deleteProject",
  async (
    { id, type }: { id: string; type: string },
    thunkAPI
  ) => {
    try {
      const response = await api.put<ApplicationResponse<string>>(
        `${AppConstants.api}/project/delete?id=${id}&type=${type}`
      );

      if (response.data.success) {
        return response.data.data || "";
      } else {
        return thunkAPI.rejectWithValue(response.data.message || "Deletion failed");
      }
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.message || "API error occurred");
    }
  }
);


export const fetchTaskList = createAsyncThunk<
  TaskModelRef[],
  {
    type: "npms" | "eci" | "custom";
    projectId: string;
  }
>("task/fetchTask", async ({ type, projectId }) => {
  const projectType = type.toUpperCase();
  const url = `${AppConstants.api}/task/list/${projectType}/${projectId}`;
  const response = await api.get<ApplicationResponse<TaskModelRef[]>>(url);
  return response.data.data || [];
});

export const fetchTaskInfo = createAsyncThunk<TaskModelRef, { taskId: string }>(
  "task/fetchTaskInfo",
  async ({ taskId }) => {
    const url = `${AppConstants.api}/task/${taskId}`;
    const response = await api.get<ApplicationResponse<TaskModelRef>>(url);

    return addDependencyName(response.data.data) as TaskModelRef;
  }
);

export const updateTaskInfo = createAsyncThunk<
  any,
  { payload: TaskModelRef; mode: string; taskId: string }
>("task/updateTaskDetails", async ({ payload, mode, taskId }) => {
  if (mode == "revision") {
    const url = `${AppConstants.api}/taskRevision` as string;
    const response = await api.post<ApplicationResponse<any>>(url, payload, {
      params: {
        taskId: taskId,
      },
    });
    return response.data.data || {};
  } else {
    const url = `${AppConstants.api}/task` as string;
    const response = await api.put<ApplicationResponse<any>>(url, payload, {
      params: {
        taskId: payload.id,
        RAR: 0,
      },
    });
    return response.data.data || {};
  }
});
export const updateTaskProgress = createAsyncThunk<
  any,
  { taskId: string; progress: number; actual_start_date?: string; actual_end_date?: string }
>(
  "task/updateTaskProgress",
  async ({ taskId, progress, actual_start_date, actual_end_date }, { rejectWithValue }) => {
    try {
      const url = `${AppConstants.api}/task/${taskId}/progress`;
      const response = await api.put<ApplicationResponse<any>>(url, null, {
        params: { progress, actual_start_date: actual_start_date ?? null, actual_end_date: actual_end_date ?? null },
      });
      return response.data.data || {};
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const checkRevisionPossible = createAsyncThunk<
  boolean,
  { taskId: string }
>(
  "task/check-revision-possible",
  async ({ taskId }) => {
    const url = `${AppConstants.api}/task-revision/revision-possible`;
    const response = await api.get<ApplicationResponse<any>>(url, { params: { id: taskId } });
    return response.data.data || false;
  }
);

export const addAndUpdateTaskDependencies = createAsyncThunk<
  any,
  {
    payload: {
      rootTaskId: string;
      dependencies: DependencyModelRef[];
    };
  }
>("task/addAndUpdateTaskDependencies", async ({ payload }) => {
  const url = `${AppConstants.api}/dependency` as string;
  const response = await api.post<ApplicationResponse<any>>(url, payload, {
    params: {
      RAR: 0,
    },
  });
  return response.data.data;
});

export const markTasksAsNotApplicable = createAsyncThunk<
  any,
  {
    taskIds: string[];
  }
>("task/mark-not-applicable", async ({ taskIds }) => {
  const payload = {
    taskIds: taskIds,
  };
  const url = `${AppConstants.api}/task/not_applicable`;
  return await api.put<ApplicationResponse<any>>(url, payload);
});

export const assignUserOrStatusUpdate = createAsyncThunk<
  any,
  BulkTaskUpdateModel
>(
  "task/assignUserOrStatusUpdate",
  async ({
    taskIds,
    userId,
    submissionDate,
    status,
    action,
    remarks,
    projectId,
    projectType,
  }) => {
    const payload: any = {
      taskIds,
    };
    if (action === "assign") {
      payload.userId = userId;
      payload.submissionDate = submissionDate;
      payload.action = ActionType.ASSIGN;
    } else {
      payload.status = status;
      payload.remarks = remarks;
    }
    const url = `${AppConstants.api}/task/update/${projectType}/${projectId}`;
    const response = await api.put<ApplicationResponse<any>>(url, payload);
    return response.data.data || [];
  }
);

export const hosApprovalStatus = createAsyncThunk<
  any,
  {
    taskIds: string[];
    user_id: string;
    approvalStatus: "approve" | "reject";
    remarks?: string;
  }
>(
  "task/hos-approval",
  async ({ taskIds, user_id, approvalStatus, remarks = "" }) => {
    const payload = {
      ids: taskIds,
      user_id,
      remarks,
    };
    const url = `${AppConstants.api}/task/${approvalStatus}`;
    return await api.put<ApplicationResponse<any>>(url, payload);
  }
);
export const revisionHosApprovalStatus = createAsyncThunk<
  any,
  {
    user_id: string;
    id: string;
    remarks?: string;
  }
>("task/revision-hos-approval", async ({ user_id, id, remarks = "" }) => {
  const payload = {
    id: id,
    user_id,
    remarks,
  };
  const url = `${AppConstants.api}/task-revision/approve`;
  return await api.put<ApplicationResponse<any>>(url, payload);
});



export const revisionHosRejectionStatus = createAsyncThunk<
  any,
  {
    user_id: string;
    id: string;
    remarks?: string;
  }
>("task/revision-hos-rejection", async ({ user_id, id, remarks = "" }) => {
  const payload = {
    id: id,
    user_id,
    remarks,
  };
  const url = `${AppConstants.api}/task-revision/reject`;
  return await api.put<ApplicationResponse<any>>(url, payload);
});







export const revisionHosApproval = createAsyncThunk<
  any,
  {
    user_id: string;
    id: string[];
    remarks?: string;
  }
>("task/project-task-approve", async ({ user_id, id, remarks = "" }) => {
  const payload = {
    ids: id,
    user_id,
    remarks,
  };
  const url = `${AppConstants.api}/task-revision/project-task-approve`;
  return await api.put<ApplicationResponse<any>>(url, payload);

});

export const revisionHosRejection = createAsyncThunk<
  any,
  {
    user_id: string;
    id: string[];
    remarks?: string;
  }
>("task/project-task-reject", async ({ user_id, id, remarks = "" }) => {
  const payload = {
    ids: id,
    user_id,
    remarks,
  };
  const url = `${AppConstants.api}/task-revision/project-task-reject`;
  return await api.put<ApplicationResponse<any>>(url, payload);
});

export const approveCustomTasks = createAsyncThunk<void, { projectId: string; payload: ApprovalPayload, action: "approve" | "reject" }>(
    "custom/task/approval",
    async ({ projectId, payload, action }, thunkAPI) => {
        const url = `${AppConstants.api}/custom/${action}/${projectId}`;
        const response = await api.put<ApplicationResponse<void>>(url, payload);
        return response.data.data;
    }
);

export const approvedByHoD = createAsyncThunk<
  any,
  {
    ids: string[];
    user_id: string;
    projectType: string;
    remarks?: string;
  }
>(
  "project/hod-approval",
  async ({ ids, user_id, projectType, remarks = "" }) => {
    const url = `${AppConstants.api}/${projectType}/approve`;

    const payload = {
      ids,
      user_id,
      remarks,
    };
    return await api.put<ApplicationResponse<any>>(url, payload);
  }
);

export const rejectedByHoD = createAsyncThunk<
  any,
  {
    projectId: string;
    task_ids: string[];
    user_id: string;
    projectType: string;
    target_date: string;
    remarks: string;

    task_remarks?: string;
    milestone_ids?: string[];
    milestone_remarks?: string;
    event_ids?: string[];
    event_remarks?: string;
  }
>(
  "project/hod-reject",
  async ({
    projectId,
    task_ids,
    task_remarks,
    user_id,
    target_date,
    projectType,
    milestone_ids,
    milestone_remarks,
    event_ids,
    event_remarks,
    remarks,
  }) => {
    const url = `${AppConstants.api}/${projectType}/reject`;

    const payload = {
      id: projectId,
      task_ids,
      user_id,
      remarks,
      target_date,
      task_remarks,
      milestone_ids,
      milestone_remarks,
      event_ids,
      event_remarks,
    };
    return await api.put<ApplicationResponse<any>>(url, payload);
  }
);

export const requestHodApproval = createAsyncThunk<
  void,
  {
    status: boolean;
    projectId: string;
    projectType: ProjectType;
    userId: string;
  }
>(
  "project/requestHodApproval",
  async ({ status, projectId, projectType, userId }) => {
    const url = `${AppConstants.api}/project/hod-approval/${projectType}/${projectId}`;
    const response = await api.put<ApplicationResponse<void>>(url, null, {
      params: {
        userId,
        status,
      },
    });
    return;
  }
);

export const getProjectApprovedByApprover = createAsyncThunk<
  any,
  {
    taskIds: string[];
    user_id: string;
    projectType: string;
    projectId: string;
    remarks?: string;
  }
>(
  "project/custom/approval",
  async ({ taskIds, user_id, projectType, projectId, remarks = "" }) => {
    const url = `${AppConstants.api}/${projectType}/approve/${projectId}`;

    const payload = {
      ids: taskIds,
      user_id,
      remarks,
    };
    return await api.put<ApplicationResponse<any>>(url, payload);
  }
);

export const getProjectApprovedByChecker = createAsyncThunk<
  any,
  {
    taskIds: string[];
    user_id: string;
    projectType: string;
    projectId: string;
    remarks?: string;
    approvalId: string;
  }
>(
  "project/custom/checker-approval",
  async ({ taskIds, user_id, projectType, projectId,approvalId, remarks = "" }) => {
    const url = `${AppConstants.api}/${projectType}/${projectId}/approval-flow/approve/${approvalId}?userId=${user_id}`;

  
    return await api.post<ApplicationResponse<any>>(url,);
  }
);


export const rejectByCheckerByOrder = createAsyncThunk<
any,
{
  taskIds: string[];
  user_id: string;
  projectType: string;
  projectId: string;
  remarks?: string;
  approvalId: string;
}
>(
"project/custom/checker-reject",
async ({ taskIds, user_id, projectType, projectId,approvalId, remarks = "" }) => {
const url = `${AppConstants.api}/${projectType}/${projectId}/approval-flow/reject/${approvalId}?userId=${user_id}`;
return await api.post<ApplicationResponse<any>>(url,null,{
  params:{
    remarks: remarks
  }
});
}
);



export const getProjectRejectedByApproverEntity = createAsyncThunk<
  any,
  {
    projectId: string;
    taskIds: string[];
    user_id: string;
    projectType: string;
    remarks: string;
  }
>(
  "project/custom/reject",
  async ({ taskIds, user_id, projectType, projectId, remarks = "" }) => {
    const url = `${AppConstants.api}/${projectType}/reject/${projectId}`;

    const payload = {
      id: projectId,
      task_ids: taskIds,
      user_id,
      remarks,
    };
    return await api.put<ApplicationResponse<any>>(url, payload);
  }
);

export const fetchDependecyList = createAsyncThunk<
  DependencyModelRef[],
  { taskId: string }
>("task/fetchDependencyList", async ({ taskId }) => {
  const url = `${AppConstants.api}/dependency`;
  const response = await api.get<ApplicationResponse<DependencyModelRef[]>>(
    url,
    {
      params: {
        taskId,
      },
    }
  );
  return (response.data.data || []) as DependencyModelRef[];
});
export const fetchTaskTree = createAsyncThunk<any[],  { taskId:string, taskType:string }
>("dashboard/fetchTaskTree", async ({taskId, taskType}) => {

  const url = `${AppConstants.api}/task/view/${taskId}?task-type=${taskType}`;
  
  const response = await api.get<ApplicationResponse<any[]>>(
    url,
    // {params: {
    //   taskType:taskType
    // }}
  );
  return response.data.data || [];
}
);


const taskSlice = createSlice({
  name: "task",
  initialState: {
    tree: { status: "loading", error: null, data: [] },
    minimal: { status: "loading", error: null, data: [] },
    info: { status: "loading", error: null, data: {} },
    revisions: { status: "loading", error: null, data: [] },
    dependencies: { status: "loading", error: null, data: [] },
  } as TaskStore,
  reducers: {
    cleanTask: (state) => {
      state.minimal = { status: "loading", error: null, data: [] };
      state.info = { status: "loading", error: null, data: {} };
    },
    updateSubtask: (state, action) => {
      state.info = {
        status: "idle",
        data: action.payload,
        error: null,
      };
    },
    updateDependencyState: (state, action) => {
      state.dependencies = {
        status: "idle",
        data: action.payload,
        error: null,
      };
    },
    overrideTaskInfo: (state, action) => {
        state.info.data = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
    .addCase(fetchTaskTree.pending, (state, action) => {
      state.tree = {
        status: "loading",
        data: [],
        error: null,
      };
    })
    .addCase(fetchTaskTree.fulfilled, (state, action) => {
      state.tree = {
        status: "idle",
        data: action.payload,
        error: null,
      };
    })
    .addCase(fetchTaskTree.rejected, (state, action) => {
      state.tree = {
        status: "failed",
        data: [],
        error: action.error,
      };
    });
    builder
      .addCase(fetchTaskList.pending, (state, action) => {
        state.minimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchTaskList.fulfilled, (state, action) => {
        state.minimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchTaskList.rejected, (state, action) => {
        state.minimal = {
          status: "failed",
          data: [],
          error: action.error,
        };
      });
    builder
      .addCase(fetchTaskInfo.pending, (state, action) => {
        state.info = {
          status: "loading",
          data: {},
          error: null,
        };
      })
      .addCase(fetchTaskInfo.fulfilled, (state, action) => {
        state.info = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchTaskInfo.rejected, (state, action) => {
        state.info = {
          status: "failed",
          data: {},
          error: action.error,
        };
      });
    builder
      .addCase(updateTaskInfo.pending, (state, action) => {
        state.info = {
          status: "loading",
          data: {},
          error: null,
        };
      })
      .addCase(updateTaskInfo.fulfilled, (state, action) => {
        state.info = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(updateTaskInfo.rejected, (state, action) => {
        state.info = {
          status: "failed",
          data: {},
          error: action.error,
        };
      })
      .addCase(fetchDependecyList.pending, (state) => {
        state.dependencies = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchDependecyList.fulfilled, (state, action) => {
        state.dependencies = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchDependecyList.rejected, (state, action) => {
        state.dependencies = {
          status: "failed",
          data: [],
          error: action.error,
        };
      });
  },
  
});
export const { updateSubtask, updateDependencyState, cleanTask, overrideTaskInfo } =
  taskSlice.actions;
export default taskSlice.reducer;
